import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowUpRight,
  Calendar,
  Edit,
  MapPin,
  Plus,
  RefreshCw,
  Wallet,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { BorderBeam } from "@/components/ui/border-beam";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import {
  getProducerDashboardStats,
  getOrganizationEvents,
  getProducerEvents,
} from "@/lib/api-producer";
import { getEventStatusLabel } from "@/components/EventStatusBadge";
import { EventPicker } from "@/components/admin/EventPicker";
import { ProducerDashboardSkeleton } from "@/components/producer/ProducerDashboardSkeleton";
import { PanelSectionHeading } from "@/components/producer/panel";
import {
  panelEase,
  panelEyebrow,
  panelPage,
  panelPill,
  panelRow,
  panelSurface,
  panelTitle,
} from "@/lib/panel-ui";
import { cn, formatBRL } from "@/lib/utils";

const TIME_ZONE = "America/Sao_Paulo";

type TFn = (key: string, options?: Record<string, unknown>) => string;

type ProducerTimelinePoint = {
  label: string;
  revenue: number;
  orders: number;
  ticketsSold: number;
  checkins: number;
};

type ProducerEventSummary = {
  id: string;
  title: string;
  status: string;
  visibility?: string | null;
  start_date: string | null;
  end_date: string | null;
  venue_city: string | null;
  grossRevenue: number;
  netRevenue: number;
  ticketRevenue: number;
  platformRevenue: number;
  gatewayFees: number;
  refundAmount: number;
  pageViews: number;
  ticketsSold: number;
  ticketsCheckedIn: number;
  conversionRate: number;
  checkinRate: number;
  capacityTotal: number;
  capacitySold: number;
  occupancyRate: number;
  remainingCapacity: number;
  daysUntilEvent: number | null;
  paidOrdersCount: number;
  paidOrdersLast7DaysCount: number;
  revenueLast7DaysAmount: number;
  paidOrdersLast30DaysCount: number;
  revenueLast30DaysAmount: number;
  couponOrdersCount: number;
  couponUseRate: number;
  hasPublishedTiers: boolean;
};

function formatPercent(value: number) {
  return `${value.toFixed(1).replace(".", ",")}%`;
}

function formatCompact(value: number) {
  return new Intl.NumberFormat("pt-BR", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value || 0);
}

function formatShortDate(value: string | null | undefined, t: TFn) {
  if (!value) return t("dashboard.dateNotSet");
  return new Date(value).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: TIME_ZONE,
  });
}

function formatLongDate(value: string | null | undefined, t: TFn) {
  if (!value) return t("dashboard.dateNotSet");
  return new Date(value).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    timeZone: TIME_ZONE,
  });
}

function getStatusLabel(
  status: string | null | undefined,
  t: TFn,
  visibility?: string | null,
) {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "active") return t("dashboard.status.active");
  if (!status) return t("dashboard.status.none");
  return getEventStatusLabel(status, visibility || undefined);
}

const chartTooltipStyle = {
  borderRadius: 16,
  border: "1px solid hsl(var(--border) / 0.6)",
  background: "hsl(var(--card))",
  fontSize: 12,
  boxShadow: "none",
};

function DashboardStat({
  label,
  value,
  support,
  hero,
  loading,
}: {
  label: string;
  value: string;
  support?: string;
  hero?: boolean;
  loading?: boolean;
}) {
  if (loading) {
    return <Skeleton className="h-32 rounded-[28px]" />;
  }

  return (
    <div className="relative h-full overflow-hidden rounded-[28px] border border-border/60 bg-card p-5 sm:p-6 transition-colors duration-150">
      {hero && <BorderBeam size={80} duration={10} />}
      <div className="relative">
        <p className={panelEyebrow}>{label}</p>
        <p
          className={cn(
            "mt-2 font-display font-semibold tabular-nums tracking-tight text-foreground",
            hero ? "text-3xl sm:text-4xl" : "text-2xl sm:text-3xl",
          )}
        >
          {value}
        </p>
        {support && (
          <p className="mt-1.5 text-xs text-muted-foreground line-clamp-1" title={support}>
            {support}
          </p>
        )}
      </div>
    </div>
  );
}

export default function ProducerDashboard() {
  const { t } = useTranslation("producerDashboard");
  const { user, profile } = useAuth();
  const reduceMotion = useReducedMotion();

  const { activeContext } = useOrganization();
  const isOrgContext =
    activeContext.type === "organization" && activeContext.organizationId;

  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [activeMetricTab, setActiveMetricTab] = useState<
    "revenue" | "orders" | "ticketsSold" | "checkins"
  >("revenue");

  const enter = (i: number) =>
    reduceMotion
      ? {}
      : {
          initial: { opacity: 0, transform: "translateY(6px)" },
          animate: { opacity: 1, transform: "translateY(0)" },
          transition: { duration: 0.22, delay: i * 0.02, ease: panelEase },
        };

  const activeMetricConfig = useMemo(
    () => ({
      revenue: {
        label: t("dashboard.pace.chart.legend.revenue"),
        formatter: (value: number) => formatBRL(value),
      },
      orders: {
        label: t("dashboard.pace.chart.legend.orders"),
        formatter: (value: number) => value.toLocaleString("pt-BR"),
      },
      ticketsSold: {
        label: t("dashboard.pace.chart.legend.ticketsSold"),
        formatter: (value: number) => value.toLocaleString("pt-BR"),
      },
      checkins: {
        label: t("dashboard.pace.chart.legend.checkins"),
        formatter: (value: number) => value.toLocaleString("pt-BR"),
      },
    }),
    [t],
  );

  // Lista de eventos para o filtro
  const { data: eventsList } = useQuery({
    queryKey: ["producer-dashboard-events", activeContext],
    queryFn: () => {
      if (isOrgContext) {
        return getOrganizationEvents(activeContext.organizationId!);
      }
      return getProducerEvents(user!.id);
    },
    enabled: isOrgContext ? !!activeContext.organizationId : !!user?.id,
    placeholderData: (previousData) => previousData,
    staleTime: 5 * 60_000,
  });

  const {
    data: stats,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: isOrgContext
      ? ["organization-dashboard", activeContext.organizationId, selectedEventId]
      : ["producer-dashboard", user?.id, selectedEventId],
    queryFn: () => {
      if (isOrgContext) {
        return getProducerDashboardStats(
          user!.id,
          activeContext.organizationId!,
          selectedEventId || undefined,
        );
      }
      return getProducerDashboardStats(
        user!.id,
        undefined,
        selectedEventId || undefined,
      );
    },
    enabled: isOrgContext ? !!activeContext.organizationId : !!user?.id,
    placeholderData: (previousData) => previousData,
    staleTime: 3 * 60_000,
    gcTime: 10 * 60_000,
  });

  if (isLoading && !stats) {
    return <ProducerDashboardSkeleton />;
  }

  const dashboard = stats as any;
  const timelineByDay = (dashboard?.timelineByDay || []) as ProducerTimelinePoint[];
  const topEvents = (dashboard?.topEvents || []) as ProducerEventSummary[];
  const focusEvent = (dashboard?.focusEvent || null) as ProducerEventSummary | null;

  const hasTimelineActivity = timelineByDay.some(
    (point) =>
      Number(point.revenue || 0) > 0 ||
      Number(point.orders || 0) > 0 ||
      Number(point.ticketsSold || 0) > 0 ||
      Number(point.checkins || 0) > 0,
  );

  const grossRevenue = Number(dashboard?.grossRevenue || dashboard?.totalRevenue || 0);
  const netRevenue = Number(dashboard?.netRevenue || 0);
  const gatewayFees = Number(dashboard?.gatewayFees || 0);
  const refundAmount = Number(dashboard?.refundAmount || 0);
  const ticketsSold = Number(dashboard?.ticketsSold || 0);
  const ticketsCheckedIn = Number(dashboard?.ticketsCheckedIn || 0);
  const conversionRate = Number(dashboard?.conversionRate || 0);
  const checkinRate = Number(dashboard?.checkinRate || 0);
  const confirmedOrders = Number(dashboard?.confirmedOrders || 0);
  const averageOrderValue = Number(dashboard?.averageOrderValue || 0);
  const ordersLast30DaysCount = Number(dashboard?.ordersLast30DaysCount || 0);
  const revenueLast30DaysAmount = Number(dashboard?.revenueLast30DaysAmount || 0);
  const ticketsSoldLast30DaysCount = Number(dashboard?.ticketsSoldLast30DaysCount || 0);
  const checkinsLast30DaysCount = Number(dashboard?.checkinsLast30DaysCount || 0);

  const focusStatusLabel = getStatusLabel(
    focusEvent?.status,
    t,
    focusEvent?.visibility,
  );

  const focusCountdown = (() => {
    if (!focusEvent) return null;
    if (focusEvent.daysUntilEvent === null || focusEvent.daysUntilEvent === undefined)
      return null;
    if (focusEvent.daysUntilEvent < 0)
      return t("dashboard.focusEvent.countdown.pastOrOngoing");
    if (focusEvent.daysUntilEvent === 0)
      return t("dashboard.focusEvent.countdown.today");
    if (focusEvent.daysUntilEvent === 1)
      return t("dashboard.focusEvent.countdown.oneDay");
    return t("dashboard.focusEvent.countdown.other", {
      count: focusEvent.daysUntilEvent,
    });
  })();

  const producerFirstName = profile?.full_name?.split(" ")[0] || "";

  return (
    <div className={panelPage}>
      {/* Header Solto & Ações Rápidas */}
      <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className={panelTitle}>{t("dashboard.header.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {producerFirstName
              ? `Olá, ${producerFirstName}. Acompanhe o desempenho geral dos seus eventos.`
              : "Acompanhe o desempenho geral e vendas dos seus eventos em tempo real."}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button asChild variant="outline" size="sm" className="rounded-full">
            <Link to="/produtor/financeiro" className="gap-1.5">
              <Wallet className="h-4 w-4" />
              {t("dashboard.header.actions.financial")}
            </Link>
          </Button>
          <Button asChild variant="outline" size="sm" className="rounded-full">
            <Link to="/produtor/eventos" className="gap-1.5">
              {t("dashboard.header.actions.events")}
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Button>
          <Button asChild size="sm" className={panelPill}>
            <Link to="/produtor/eventos/novo" className="gap-1.5">
              <Plus className="h-4 w-4" />
              {t("dashboard.header.actions.createEvent")}
            </Link>
          </Button>
        </div>
      </header>

      {/* Seletor de Evento */}
      <div className="flex items-center justify-between gap-3">
        <div className="relative w-full sm:w-80">
          <EventPicker
            value={selectedEventId}
            onValueChange={setSelectedEventId}
            events={eventsList || []}
            placeholder={t("dashboard.eventPicker.allEvents")}
            className="h-11 rounded-2xl border-border/60 bg-white dark:bg-card text-sm shadow-none hover:bg-secondary/40"
          />
          {isFetching && !isLoading && (
            <div className="pointer-events-none absolute right-9 top-1/2 -translate-y-1/2">
              <RefreshCw className="h-3.5 w-3.5 animate-spin text-muted-foreground/60" />
            </div>
          )}
        </div>
      </div>

      {/* Mensagem de Erro (se houver) */}
      {error ? (
        <div className="flex items-center justify-between rounded-[22px] border border-destructive/30 bg-destructive/10 p-4 text-sm">
          <div>
            <p className="font-semibold text-destructive">{t("dashboard.error.title")}</p>
            <p className="text-xs text-muted-foreground">{t("dashboard.error.description")}</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full gap-1.5"
            onClick={() => refetch()}
          >
            <RefreshCw className="h-3.5 w-3.5" />
            {t("dashboard.error.reload")}
          </Button>
        </div>
      ) : null}

      {/* Grid de 4 Métricas Principais */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <motion.div {...enter(0)}>
          <DashboardStat
            hero
            label={t("dashboard.metrics.netRevenue.label")}
            value={formatBRL(netRevenue)}
            support={
              grossRevenue > 0
                ? `Faturamento bruto: ${formatBRL(grossRevenue)}`
                : gatewayFees > 0 || refundAmount > 0
                  ? `Taxas: ${formatBRL(gatewayFees)} · Estornos: ${formatBRL(refundAmount)}`
                  : "Receita após taxas deduzidas"
            }
            loading={isLoading}
          />
        </motion.div>

        <motion.div {...enter(1)}>
          <DashboardStat
            label={t("dashboard.metrics.ticketsSold.label")}
            value={ticketsSold.toLocaleString("pt-BR")}
            support={
              ticketsSoldLast30DaysCount > 0
                ? `${ticketsSoldLast30DaysCount.toLocaleString("pt-BR")} nos últimos 30 dias`
                : conversionRate > 0
                  ? `Conversão de ${formatPercent(conversionRate)}`
                  : "Ingressos emitidos"
            }
            loading={isLoading}
          />
        </motion.div>

        <motion.div {...enter(2)}>
          <DashboardStat
            label={t("dashboard.metrics.paidOrders.label")}
            value={confirmedOrders.toLocaleString("pt-BR")}
            support={
              averageOrderValue > 0
                ? `Ticket médio de ${formatBRL(averageOrderValue)}`
                : ordersLast30DaysCount > 0
                  ? `${ordersLast30DaysCount} nos últimos 30 dias`
                  : "Pedidos confirmados"
            }
            loading={isLoading}
          />
        </motion.div>

        <motion.div {...enter(3)}>
          <DashboardStat
            label={t("dashboard.metrics.checkins.label")}
            value={formatPercent(checkinRate)}
            support={`${ticketsCheckedIn.toLocaleString("pt-BR")} check-ins realizados`}
            loading={isLoading}
          />
        </motion.div>
      </div>

      {/* Evento em Foco / Selecionado */}
      <motion.div {...enter(4)} className={panelSurface}>
        <PanelSectionHeading>
          {selectedEventId ? "Evento selecionado" : t("dashboard.focusEvent.title")}
        </PanelSectionHeading>

        <div className="p-5 sm:p-6">
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-16 w-full rounded-2xl" />
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-20 rounded-2xl" />
                ))}
              </div>
            </div>
          ) : focusEvent ? (
            <div className="space-y-5">
              {/* Cabeçalho do evento */}
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 space-y-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className="rounded-full px-2.5 py-0.5 text-xs font-normal">
                      {focusStatusLabel}
                    </Badge>
                    {focusCountdown && (
                      <Badge variant="secondary" className="rounded-full px-2.5 py-0.5 text-xs font-normal">
                        {focusCountdown}
                      </Badge>
                    )}
                  </div>
                  <h3 className="truncate font-display text-xl font-semibold tracking-tight text-foreground">
                    {focusEvent.title}
                  </h3>
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Calendar className="h-3.5 w-3.5 shrink-0" />
                    <span>{formatLongDate(focusEvent.start_date, t)}</span>
                    {focusEvent.venue_city && (
                      <>
                        <span>·</span>
                        <MapPin className="h-3.5 w-3.5 shrink-0" />
                        <span>{focusEvent.venue_city}</span>
                      </>
                    )}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2 shrink-0">
                  <Button asChild variant="outline" size="sm" className="rounded-full">
                    <Link to={`/produtor/eventos/${focusEvent.id}/editar`} className="gap-1.5">
                      <Edit className="h-3.5 w-3.5" />
                      {t("dashboard.focusEvent.actions.edit")}
                    </Link>
                  </Button>
                  <Button asChild size="sm" className={panelPill}>
                    <Link to={`/produtor/eventos/${focusEvent.id}/painel`} className="gap-1.5">
                      {t("dashboard.focusEvent.actions.panel")}
                      <ArrowUpRight className="h-3.5 w-3.5" />
                    </Link>
                  </Button>
                </div>
              </div>

              {/* Wells de métricas consolidadas do evento */}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="rounded-2xl bg-secondary/50 p-4">
                  <p className={panelEyebrow}>Receita líquida</p>
                  <p className="mt-1.5 font-display text-xl font-semibold tabular-nums tracking-tight text-foreground">
                    {formatBRL(focusEvent.netRevenue)}
                  </p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    {formatBRL(focusEvent.grossRevenue)} bruto
                  </p>
                </div>

                <div className="rounded-2xl bg-secondary/50 p-4">
                  <p className={panelEyebrow}>Ingressos vendidos</p>
                  <p className="mt-1.5 font-display text-xl font-semibold tabular-nums tracking-tight text-foreground">
                    {focusEvent.ticketsSold.toLocaleString("pt-BR")}
                  </p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    {formatPercent(focusEvent.occupancyRate)} ocupação
                  </p>
                </div>

                <div className="rounded-2xl bg-secondary/50 p-4">
                  <p className={panelEyebrow}>Pedidos pagos</p>
                  <p className="mt-1.5 font-display text-xl font-semibold tabular-nums tracking-tight text-foreground">
                    {focusEvent.paidOrdersCount.toLocaleString("pt-BR")}
                  </p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    Ticket médio {formatBRL(focusEvent.ticketRevenue / Math.max(focusEvent.ticketsSold, 1))}
                  </p>
                </div>

                <div className="rounded-2xl bg-secondary/50 p-4">
                  <p className={panelEyebrow}>Comparecimento</p>
                  <p className="mt-1.5 font-display text-xl font-semibold tabular-nums tracking-tight text-foreground">
                    {formatPercent(focusEvent.checkinRate)}
                  </p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    {focusEvent.ticketsCheckedIn.toLocaleString("pt-BR")} check-ins
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <p className="font-medium text-foreground">{t("dashboard.focusEvent.empty.title")}</p>
              <p className="mt-1 text-sm text-muted-foreground max-w-sm">
                {t("dashboard.focusEvent.empty.description")}
              </p>
              <Button asChild size="sm" className="mt-4 rounded-full">
                <Link to="/produtor/eventos/novo" className="gap-1.5">
                  <Plus className="h-4 w-4" />
                  {t("dashboard.focusEvent.empty.cta")}
                </Link>
              </Button>
            </div>
          )}
        </div>
      </motion.div>

      {/* Ritmo de Vendas (Gráfico Diário) */}
      <motion.div {...enter(5)} className={panelSurface}>
        <div className="flex flex-col gap-3 border-b border-border/60 p-5 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-4">
          <h2 className="flex items-center gap-2.5">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
            <span className="font-display text-base font-semibold tracking-tight">
              {t("dashboard.pace.title")}
            </span>
          </h2>

          {/* Abas Pílula de Métrica */}
          <div className="flex flex-wrap items-center gap-1 rounded-full bg-secondary/70 p-1">
            <button
              type="button"
              onClick={() => setActiveMetricTab("revenue")}
              className={cn(
                "rounded-full px-3.5 py-1 text-xs font-medium transition-colors duration-150",
                activeMetricTab === "revenue"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              Receita ({formatBRL(revenueLast30DaysAmount)})
            </button>
            <button
              type="button"
              onClick={() => setActiveMetricTab("orders")}
              className={cn(
                "rounded-full px-3.5 py-1 text-xs font-medium transition-colors duration-150",
                activeMetricTab === "orders"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              Pedidos ({ordersLast30DaysCount})
            </button>
            <button
              type="button"
              onClick={() => setActiveMetricTab("ticketsSold")}
              className={cn(
                "rounded-full px-3.5 py-1 text-xs font-medium transition-colors duration-150",
                activeMetricTab === "ticketsSold"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              Ingressos ({ticketsSoldLast30DaysCount})
            </button>
            <button
              type="button"
              onClick={() => setActiveMetricTab("checkins")}
              className={cn(
                "rounded-full px-3.5 py-1 text-xs font-medium transition-colors duration-150",
                activeMetricTab === "checkins"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              Check-ins ({checkinsLast30DaysCount})
            </button>
          </div>
        </div>

        <div className="p-5 sm:p-6">
          {isLoading ? (
            <Skeleton className="h-64 w-full rounded-2xl" />
          ) : !hasTimelineActivity ? (
            <div className="flex h-64 flex-col items-center justify-center text-center">
              <p className="text-sm font-medium text-foreground">
                {t("dashboard.pace.chart.empty.title")}
              </p>
              <p className="mt-1 text-xs text-muted-foreground max-w-sm">
                Assim que houver novas vendas ou movimentações nos últimos 30 dias, o gráfico exibirá a curva diária.
              </p>
            </div>
          ) : (
            <div className="h-64 sm:h-72">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={240} debounce={50}>
                <AreaChart
                  data={timelineByDay}
                  margin={{ top: 12, right: 12, left: -16, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="colorMetric" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border) / 0.4)" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    width={56}
                    domain={[0, "dataMax + 1"]}
                    tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(value) =>
                      activeMetricTab === "revenue"
                        ? formatCompact(Number(value))
                        : Number(value).toLocaleString("pt-BR")
                    }
                  />
                  <Tooltip
                    contentStyle={chartTooltipStyle}
                    formatter={(value: number) => [
                      activeMetricConfig[activeMetricTab].formatter(value),
                      activeMetricConfig[activeMetricTab].label,
                    ]}
                  />
                  <Area
                    type="monotone"
                    dataKey={activeMetricTab}
                    name={activeMetricConfig[activeMetricTab].label}
                    stroke="hsl(var(--primary))"
                    fill="url(#colorMetric)"
                    strokeWidth={2.5}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </motion.div>

      {/* Top Eventos do Portfólio */}
      <motion.div {...enter(6)} className={panelSurface}>
        <PanelSectionHeading>{t("dashboard.topEvents.title")}</PanelSectionHeading>

        <div className="p-0">
          {isLoading ? (
            <div className="space-y-3 p-5 sm:p-6">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full rounded-2xl" />
              ))}
            </div>
          ) : topEvents.length ? (
            <>
              {/* Tabela Desktop */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border/50 text-left text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
                      <th className="px-6 py-3.5 font-medium">{t("dashboard.topEvents.table.event")}</th>
                      <th className="px-4 py-3.5 font-medium">{t("dashboard.topEvents.table.netRevenue")}</th>
                      <th className="px-4 py-3.5 font-medium">{t("dashboard.topEvents.table.ticketsAndOccupancy")}</th>
                      <th className="px-4 py-3.5 font-medium">{t("dashboard.topEvents.table.conversionAndCheckin")}</th>
                      <th className="px-4 py-3.5 font-medium">{t("dashboard.topEvents.table.status")}</th>
                      <th className="px-6 py-3.5 text-right font-medium">{t("dashboard.topEvents.table.action")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topEvents.map((event) => (
                      <tr
                        key={event.id}
                        className="border-t border-border/50 transition-colors duration-150 hover:bg-secondary/30"
                      >
                        <td className="px-6 py-4">
                          <div className="space-y-0.5">
                            <p className="font-medium text-foreground">{event.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {formatShortDate(event.start_date, t)}
                              {event.venue_city ? ` · ${event.venue_city}` : ""}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <p className="font-semibold tabular-nums text-foreground">
                            {formatBRL(event.netRevenue)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatBRL(event.grossRevenue)} bruto
                          </p>
                        </td>
                        <td className="px-4 py-4 text-muted-foreground">
                          <p className="text-foreground">
                            {event.ticketsSold.toLocaleString("pt-BR")} ingressos
                          </p>
                          <p className="text-xs">
                            {formatPercent(event.occupancyRate)} ocupação
                          </p>
                        </td>
                        <td className="px-4 py-4 text-muted-foreground">
                          <p className="text-foreground">
                            {formatPercent(event.conversionRate)} conversão
                          </p>
                          <p className="text-xs">
                            {formatPercent(event.checkinRate)} check-in
                          </p>
                        </td>
                        <td className="px-4 py-4">
                          <Badge variant="outline" className="rounded-full px-2.5 py-0.5 text-xs font-normal">
                            {getStatusLabel(event.status, t, event.visibility)}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <Button asChild variant="ghost" size="sm" className="rounded-full gap-1">
                            <Link to={`/produtor/eventos/${event.id}/painel`}>
                              Abrir painel
                              <ArrowUpRight className="h-3.5 w-3.5" />
                            </Link>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Lista Mobile */}
              <div className="md:hidden space-y-3 p-4">
                {topEvents.map((event) => (
                  <div key={event.id} className={cn(panelRow, "p-4 space-y-3")}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-foreground truncate">{event.title}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {formatShortDate(event.start_date, t)}
                          {event.venue_city ? ` · ${event.venue_city}` : ""}
                        </p>
                      </div>
                      <Badge variant="outline" className="shrink-0 rounded-full px-2 py-0.5 text-xs font-normal">
                        {getStatusLabel(event.status, t, event.visibility)}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-2 gap-2.5 py-2.5 border-y border-border/50 text-xs">
                      <div>
                        <p className="text-muted-foreground">Receita líquida</p>
                        <p className="font-semibold text-foreground mt-0.5">{formatBRL(event.netRevenue)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Ingressos</p>
                        <p className="font-semibold text-foreground mt-0.5">{event.ticketsSold} vendidos</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Ocupação</p>
                        <p className="font-semibold text-foreground mt-0.5">{formatPercent(event.occupancyRate)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Check-in</p>
                        <p className="font-semibold text-foreground mt-0.5">{formatPercent(event.checkinRate)}</p>
                      </div>
                    </div>

                    <Button asChild variant="outline" size="sm" className="w-full rounded-full gap-1.5">
                      <Link to={`/produtor/eventos/${event.id}/painel`}>
                        Abrir painel <ArrowUpRight className="h-3.5 w-3.5" />
                      </Link>
                    </Button>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="p-8 text-center">
              <p className="font-medium text-foreground">{t("dashboard.topEvents.empty.title")}</p>
              <p className="mt-1 text-sm text-muted-foreground">{t("dashboard.topEvents.empty.description")}</p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
